import type { ColorId } from "./colors.ts";
import type { Experience } from "./parse.ts";

export type Tier = "lab" | "cards";
export type BlockKind = "test" | "practice" | "audit" | "sighted";
export type CallMode = "builtin" | "external";
export type Phase = "idle" | "lobby" | "ready" | "card_up" | "locking" | "reveal" | "block_done";
export type GuessSource = "voice" | "partner" | "guesser_tap";
export type TargetSource = "csprng" | "cv" | "partner";
export type Condition = "open" | "sham";

/** Ginette's progression, from the 2026-10-24 workshop notes. */
export const STAGES = [
  { id: "none", label: "Nothing", hint: "Dark, no change" },
  { id: "contrast", label: "Contrast", hint: "Light vs dark areas" },
  { id: "sparkles", label: "Sparkles", hint: "Pinpoints, small openings" },
  { id: "clouds", label: "Colour clouds", hint: "Colour as mist" },
  { id: "shapes", label: "Shapes", hint: "Forms and edges" },
  { id: "realtime", label: "Real-time sight", hint: "Clear environment" },
] as const;
export type StageId = (typeof STAGES)[number]["id"];

export const STATE_TAGS = ["meditated", "well slept", "tired", "caffeine", "stressed", "playful"] as const;

export interface Me { id: string; name: string; others: { id: string; name: string }[] }

export interface PublicTrial {
  id: number;
  idx: number;
  dealtAt: number;
  guess: ColorId | null;
  guessSource: GuessSource | null;
  experience: Experience | null;
  transcript: string | null;
  /** Null until reveal, except for the partner in cards mode, who can see the card. */
  target: ColorId | null;
  targetSource: TargetSource | null;
  condition: Condition | null;
  hit: boolean | null;
  lockAt: number | null;
}

export interface PublicBlock {
  id: number;
  idx: number;
  kind: BlockKind;
  guesserId: string;
  partnerId: string;
  planned: number;
  done: number;
  hits: number;
  rig: string;
  tags: string[];
  ribbon: { target: ColorId | null; guess: ColorId | null; hit: boolean | null; sham: boolean }[];
}

export interface PublicSession {
  id: number;
  tier: Tier;
  palette: ColorId[];
  callMode: CallMode;
  trialsPerTurn: number;
  witness: string | null;
  autoAdvance: boolean;
}

export interface PublicState {
  phase: Phase;
  paused: boolean;
  session: PublicSession | null;
  block: PublicBlock | null;
  trial: PublicTrial | null;
  presence: Record<string, { room: boolean; card: boolean }>;
  sayAgain: number;
  serverNow: number;
  lastBlockId: number | null;
  /** Who guessed last, so the setup can default to swapping. */
  lastGuesserId: string | null;
  /** Attack result per "guesser:rig". Sent only in the lobby. */
  rigStatus: Record<string, string>;
  /** Each person's most recent rig name. */
  rigs: Record<string, string | null>;
  demo: boolean;
}

export type ClientMsg =
  | { t: "start_session"; tier: Tier; palette: ColorId[]; trialsPerTurn: number; callMode: CallMode; witness?: string | null }
  | { t: "start_block"; guesserId: string; kind: BlockKind; planned?: number; rig: string; tags?: string[] }
  | { t: "next" }
  | { t: "pause" }
  | { t: "resume" }
  | { t: "guess"; color: ColorId; source: "partner" | "guesser_tap"; experience?: Experience }
  | { t: "void"; reason?: string }
  | { t: "cv"; color: ColorId; confidence: number }
  | { t: "target"; color: ColorId }
  | { t: "correct"; trialId: number; color: ColorId; field: "guess" | "target" }
  | { t: "end_block" }
  | { t: "close_block" }
  | { t: "end_session" }
  | { t: "journal"; blockId: number; stage: StageId; note?: string }
  | { t: "signal"; data: unknown }
  | { t: "ping" };

export type ServerMsg =
  | { t: "state"; state: PublicState }
  /** Sent only to the current guesser's card device. Null means a dark screen. */
  | { t: "card"; hex: string | null; on: boolean }
  | { t: "signal"; from: string; data: unknown }
  | { t: "peer"; id: string; present: boolean }
  | { t: "error"; message: string };

// ----- dashboard ----------------------------------------------------------

export interface TrialRow {
  id: number;
  sessionId: number;
  blockId: number;
  tier: Tier;
  kind: BlockKind;
  guesserId: string;
  rig: string;
  tags: string[];
  witness: string | null;
  callMode: CallMode;
  stage: string | null;
  k: number;
  idx: number;
  condition: Condition;
  target: ColorId | null;
  /** Second draw, never shown. Guesses should match it at exactly chance (Hyman's cross-check). */
  controlTarget: ColorId | null;
  targetSource: TargetSource;
  targetConfidence: number | null;
  guess: ColorId | null;
  guessSource: GuessSource | null;
  experience: Experience | null;
  transcript: string | null;
  hit: boolean | null;
  dealtAt: number;
  guessedAt: number | null;
  voided: boolean;
  /** A note that the locked answer was misheard. It never changes the score. */
  corrected: boolean;
  correctedGuess: ColorId | null;
  correctedTarget: ColorId | null;
}
